/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-indent */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Form as FinalForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import LoadingButton from '@mui/lab/LoadingButton';
import { useConfirm } from 'material-ui-confirm';
import { getDetailsGql, updateCheckoutGql, createPaymentIntentGql } from './gql';
import ChooseTicketCategory from './components/ChooseTicketCategory';
import EventDetails from './components/EventDetails';
import AttendeeDetails from './components/AttendeeDetails';
import InvoiceAddress from './components/InvoiceAddress';
import { formatCheckoutDataToState, formatStateToCheckout } from './helper';
import Review from './components/Review';
import { LayoutContext } from '../../stores/LayoutStore';
import Tracking from '../../components/Tracking';

function CheckoutPage({ stripePromise }) {
  const params = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeElements, setStripeElements] = useState(null);
  const [nextButtonActive, setNextButtonActive] = useState(false);
  const formRef = useRef();
  const contentRef = useRef();
  const { t } = useTranslation('checkout');
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const layoutContext = useContext(LayoutContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, refetch, loading } = useQuery(getDetailsGql, {
    variables: {
      slug: params.slug,
    },
  });

  const [updateCheckout, { loading: loadingUpdateCheckout }] = useMutation(updateCheckoutGql, {
    variables: { slug: params.slug },
  });

  const [createPaymentIntent, { loading: loadingPaymentIntent }] = useMutation(createPaymentIntentGql, {
    variables: { slug: params.slug },
  });

  const details = useMemo(() => {
    if (data?.getDetails) {
      return {
        ...data?.getDetails,
        products: data.getDetails.products.map((orgProduct) => {
          const product = { ...orgProduct };
          const avail = data.getAvailabilities.availabilities.find(({ productId }) => productId === product.id);
          product.isAvailable = avail?.isAvailable;
          product.maxQuantity = avail?.maxQuantity;
          return product;
        }),
      };
    }
    return {};
  }, [data?.getDetails, data?.getAvailabilities?.availabilities]);

  const initValues = useMemo(() => formatCheckoutDataToState(data), [data?.getDetails]);

  const steps = (params.slug === 'kongress-bw-2025-demo')
    ? [t('steps.chooseTicket'), t('steps.enterAttendee'), 'Foren', t('steps.invoiceAddress'), t('steps.summary')]
    : [t('steps.chooseTicket'), t('steps.enterAttendee'), t('steps.invoiceAddress'), t('steps.summary')];

  const getStepFromLocation = () => {
    const stepMatch = location.hash.match(/step-(\d+)/);
    return stepMatch ? Math.min(parseInt(stepMatch[1], 10), steps.length - 1) : 0;
  };

  useEffect(() => {
    setActiveStep(getStepFromLocation());
  }, [location.hash]);

  const handleBack = () => {
    if (formRef.current.getState().invalid) formRef.current.reset(formatCheckoutDataToState(data));
    if (activeStep > 0) navigate(`/${params.slug}/checkout#step-${activeStep - 1}`);
  };

  useEffect(() => {
    layoutContext.setSlug(params.slug);
  }, [params.slug]);

  useEffect(() => {
    if (searchParams.get('error') !== 'failedPayment' || loading) return;
    setSearchParams((params) => {
      params.delete('error');
      return params;
    });
    navigate(`/${params.slug}/checkout#step-${steps.length - 1}`, { replace: true });
    contentRef.current?.scrollIntoView();
    confirm({
      title: t('paymentErrors.headline'),
      description: t('paymentErrors.general'),
      hideCancelButton: true,
    });
  }, [searchParams, loading]);

  const onSubmit = async (values) => {
    if (activeStep === 3) {
      let confirmationToken;
      if (data?.getCheckout?.total > 0) {
        setStripeLoading(true);
        const { error: submitError } = await stripeElements.submit();
        if (submitError) {
          setStripeLoading(false);
          return;
        }

        const { error, confirmationToken: token } = await stripe.createConfirmationToken({
          elements: stripeElements,
          return_url: `${window.location.origin}/payment-complete/${params.slug}/stripe`,
        });
        confirmationToken = token;
        if (error) {
          setStripeLoading(false);
          return;
        }
      }
      try {
        const { data } = await createPaymentIntent({
          variables: { slug: params.slug, input: { confirmationTokenId: confirmationToken?.id } },
        });

        if (data?.createPaymentIntent?.status === 'requires_action') {
          /* const {
            error,
            paymentIntent,
          } =  */await stripe.handleNextAction({
            clientSecret: data?.createPaymentIntent?.clientSecret,
          });
          return;
        }

        window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
        return;
      } catch (error) {
        setStripeLoading(false);
        if (error?.cause?.result?.code === 'InsufficientInventory') {
          const { data } = await refetch();
          const availabilities = data?.getAvailabilities?.availabilities;
          await confirm({
            title: t('error'),
            description: (
              <>
                <Typography gutterBottom>{t('insufficientInventory')}</Typography>
                <Typography sx={{ marginTop: 1 }}>{t('insufficientInventoryAffectdCategories')}</Typography>
                <ul>
                  {error?.cause?.result?.unavailableProducts?.map((productId) => (
                    <li key={productId}>
                      {details?.products?.find(({ id }) => productId === id)?.name}
                    </li>
                  ))}
                </ul>
              </>
            ),
            hideCancelButton: true,
          });
          for (const productId of error.cause.result.unavailableProducts) {
            const avail = availabilities.find((row) => row.productId === productId);
            formRef.current.change(`product_${productId}`, Math.min(avail.maxQuantity, values[`product_${productId}`]));
          }
          navigate(`/${params.slug}/checkout#step-0`);
          await updateCheckout({
            variables: { slug: params.slug, input: formatStateToCheckout(formRef.current.getState().values) },
          });
        } else {
          const checkoutErrorCode = error?.networkError?.result?.code;
          const paymentErrorCode = checkoutErrorCode
            || error?.cause?.result?.declineCode
            || error?.cause?.result?.stripeCode;
          const modalTitle = checkoutErrorCode
            ? t(['checkoutErrors.headline', 'paymentErrors.headline'])
            : t('paymentErrors.headline');
          const modalMessage = t([`coupon.errors.${paymentErrorCode}`, `paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
          await confirm({
            title: modalTitle,
            description: modalMessage,
            hideCancelButton: true,
          });
        }
        setStripeLoading(false);
      }
      return;
    }

    if (steps.length !== (activeStep - 1)) {
      await updateCheckout({
        variables: { slug: params.slug, input: formatStateToCheckout(values) },
      });

      await refetch();

      if (activeStep < steps.length - 1) navigate(`/${params.slug}/checkout#step-${activeStep + 1}`);
    }
  };

  const renderContent = (values) => {
    if (loadingUpdateCheckout || loading) {
      return (
        <Grid sx={{ width: '100%', textAlign: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Grid>
      );
    }

    if (activeStep === 0) {
      return (<ChooseTicketCategory details={details} values={values} setNextButtonActive={setNextButtonActive} />);
    }
    if (activeStep === 1) {
      return (<AttendeeDetails details={details} values={values} setNextButtonActive={setNextButtonActive} />);
    }
    if (activeStep === 2) {
      return (<InvoiceAddress details={details} values={values} />);
    }

    if (activeStep === 3) {
      if (data.getCheckout.total > 0) {
        const options = {
          mode: 'payment',
          amount: data.getCheckout.total * 100,
          currency: 'eur',
          paymentMethodCreation: 'manual',
          payment_method_types: details.event.paymentMethods,
          appearance: {
            variables: {
              colorPrimary: details?.event?.colors?.primary || '#1976d2',
            },
            theme: 'stripe',
          },
        };
        return (
          <Elements options={options} stripe={stripePromise}>
            <Review
              checkout={data?.getCheckout}
              details={details}
              values={values}
              setStripe={setStripe}
              setStripeElements={setStripeElements}
              refetchParentCheckout={refetch}
            />
          </Elements>
        );
      }
      return (
        <Review
          checkout={data?.getCheckout}
          details={details}
          values={values}
          setStripe={setStripe}
          setStripeElements={setStripeElements}
          refetchParentCheckout={refetch}
        />
      );
    }
    return null;
  };

  return (
    <FinalForm
      initialValues={initValues}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, form }) => {
        useEffect(() => {
          formRef.current = form;
        }, [form]);
        return (
          <form onSubmit={handleSubmit}>
            <Tracking details={details} />
            <EventDetails details={details} />
            <Box
              sx={{
                ml: 10,
                p: { xs: 2, md: 4 },
                maxWidth: 800,
                mx: 'auto',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 3,
              }}
              ref={contentRef}
            >
              <Stepper activeStep={activeStep} sx={{ mb: 4, marginX: { xs: -2, md: 0 } }} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label} sx={{ maxWidth: { xs: '100px', md: 'unset' }, hyphens: { xs: 'auto', md: 'unset' } }}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {renderContent(values)}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                {activeStep !== 0 ? (
                  <Button
                    disabled={activeStep === 0 || stripeLoading || loadingUpdateCheckout}
                    onClick={handleBack}
                    variant="outlined"
                  >
                    {t('back')}
                  </Button>
                ) : (<div />)}
                {activeStep === steps.length - 1
                  ? (stripeLoading) ? (
                    <LoadingButton loading variant="outlined">
                      Submit
                    </LoadingButton>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={stripeLoading}
                    >
                      {t('submit')}
                    </Button>
                  )
                  : (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!nextButtonActive || stripeLoading || loadingUpdateCheckout || loadingPaymentIntent}
                    type="submit"
                  >
                    {t('next')}
                  </Button>
                  )}
              </Box>
            </Box>
          </form>
        );
      }}
    </FinalForm>
  );
}

export default CheckoutPage;
